import { encounterField, encounterCreatureField } from '../../shared/array-types';
import { asRow } from '../minigames/rows';
import { isRefusalCode, REFUSAL_PROSE_WORDS } from './refusal-code';

export const REFUSAL_KINDS = ['refused', 'skipped', 'page_error', 'rejection', 'slow', 'warning'] as const;
export type RefusalKind = typeof REFUSAL_KINDS[number];
export interface RefusalInput {
    id: string; kind: unknown; reason: unknown; detail?: unknown; page: string; surface: unknown;
    encounter_id?: unknown; actor_id?: unknown; ability_id?: unknown; client_ms?: unknown; build?: unknown; engine?: unknown;
}
const uuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
export const maskTelemetryText = (value: string, limit: number): string => Array.from(value
    .replace(/\b(?:Bearer|Basic)\s+[A-Za-z0-9+/_=.\-]+/gi, '*')
    .replace(/\b(password|passwd|token|api[_-]?key|authorization|secret|session|(?:campaign[_-]?)?invite[_-]?code|(?:player[_-]?|gm[_-]?)?email(?:[_-]?updates)?|(?:player[_-]?geoplugin[_-]?)?timezone)["']?\s*[=:]\s*(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s<>&,;]+)/gi, '$1=*')
    .replace(/\S+/g, token => {
        const path = token.split(/[?#]/)[0] ?? token;
        return /[\\/]|\.[A-Za-z0-9_-]+$/.test(path) ? path : token;
    })
    .replace(/[\p{L}\p{N}\p{M}._%+\-]+@[\p{L}\p{N}\p{M}.。．｡\-]+[.。．｡][\p{L}\p{N}\p{M}\-]{2,}/gu, '*')
    .replace(/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi, '*')
    .replace(/[\p{C}]/gu, ' ')).slice(0, limit).join('');
export const telemetryBasename = (value: string): string => value.split(/[?#]/)[0]?.split(/[\\/]/).pop() ?? '';
/** One diagnostic source location, never the stack body, URL query or function arguments. */
function pageErrorReason(value: string, detail: unknown): string {
    const row = asRow(detail);
    const firstFrame = typeof row?.stack === 'string' ? row.stack.split('\n')
        .find(line => /^\s*at\s|^[^@\s]*@/.test(line)) : undefined;
    const frame = firstFrame ? /(?:^|[\s(@])([^\s()]+?):(\d+)(?::(\d+))?\)?$/.exec(firstFrame) : null;
    const file = maskTelemetryText(telemetryBasename(frame?.[1] ?? (typeof row?.file === 'string' ? row.file : '')), 96);
    const line = frame ? Number(frame[2]) : duration(row?.line);
    const column = frame?.[3] ? Number(frame[3]) : duration(row?.col);
    const source = file && /^[a-z0-9_.\-*]+$/i.test(file) && line !== null && Number.isSafeInteger(line) && line > 0
        ? ` @ ${file}:${line}${column !== null && Number.isSafeInteger(column) && column > 0 ? ':' + column : ''}` : '';
    const message = maskTelemetryText(value.split(/[\r\n]/)[0] ?? '', 191 - Array.from(source).length).trim() || 'Error';
    return message + source;
}
const duration = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.min(4294967295, Math.floor(value)) : null;
const identity = (value: unknown): string | null => typeof value === 'string' && uuid.test(value) ? value.toUpperCase() : null;
const tag = (value: unknown): string | null => typeof value === 'string' && /^[\x20-\x7e]*$/.test(value) ? maskTelemetryText(value, 64) : null;

/** Code labels only. Operation identities are tab-local aliases, never database UUIDs. */
export const telemetryCode = (value: unknown): string | null => typeof value === 'string'
    && /^[a-z0-9_$.:*-]{1,64}$/i.test(value) ? maskTelemetryText(value, 64) : null;
export interface TelemetryAction { lane: string; schema: string; operation_id: string; outcome: string; reason?: string }
export const TRUTH_OUTCOMES = ['sending', 'accepted', 'committed', 'refused', 'stale', 'failed', 'unknown'] as const;

// UI prompts and GM session notices stay visible as instructions. Errors remain bug signals.
const instructionPrefixes = ['Click a creature to attach the aura', 'Click a spot in range to cast from',
    'Lob — the throw arcs over cover (L: straight)', 'Straight throw — needs a clear line (L: lob)',
    'Choose a creature before ending its turn.', 'Pick a table to roll.', 'Select ', 'Control a character first',
    'The GM has paused play.', 'The GM has resumed play.', 'The GM has entered Theatre of the Mind.',
    'Target exactly one light source on the map (click token to toggle target).',
    'Reveal this opening before using it.',
    'Stand within reach of this opening.', 'Move within reach to use this light source.', 'Move within reach to peek in bags.',
    'Move closer to take inventory.', 'Move closer to take from, place into, or hand over that inventory.',
    'Take control of a character first ("Act as this creature"), then open trade / training.',
    'You are currently acting AS this merchant/trainer — switch to the buying character first.', 'Move cancelled.'];
export const warningIsInstruction = (message: string): boolean => instructionPrefixes.some(prefix => message.startsWith(prefix));

/** Skip the wrapper frame; retain a function name only, never a source URL or stack body. */
export function warningCaller(stack: string | undefined): string | null {
    const frames = (stack ?? '').split('\n');
    const frame = frames[frames[0]?.startsWith('Error') ? 2 : 1] ?? '';
    const name = /^\s*at (?:async )?([\w$]+(?:\.[\w$]+)*)\s+\(/.exec(frame)?.[1]
        ?? /^([\w$]+(?:\.[\w$]+)*)@/.exec(frame)?.[1];
    // An anonymous top caller has no name; never substitute a deeper, unrelated frame.
    return telemetryCode(name);
}

const errorNames = 'Error|EvalError|RangeError|ReferenceError|SyntaxError|TypeError|URIError|AggregateError|InternalError|NetworkError|AbortError|DOMException';
const errorEnvelope = new RegExp(`^(?:Uncaught(?: \\(in promise\\))? )?(?:(${errorNames}): *)?([\\s\\S]*)$`);
const identifier = '[A-Za-z_$][A-Za-z0-9_$.]{0,63}';
const slot = `(?:${identifier}|\\?)`, quotedSlot = `(?:'${identifier}'|\\?)`;
const errorTemplates = ([
    [`Cannot (read|set) properties of (undefined|null) \\((reading|setting) ${quotedSlot}\\)`, 'Cannot $1 properties of $2 ($3 ?)'],
    [`Cannot (read|set) property ${quotedSlot} of (undefined|null)`, 'Cannot $1 property ? of $2'],
    [`${slot} is not (a function|defined)`, '? is not $1'],
    [`Can't find variable: ${slot}`, "Can't find variable: ?"],
    [`Cannot access ${quotedSlot} before initialization`, 'Cannot access ? before initialization'],
    [`can't access lexical declaration ${quotedSlot} before initialization`, "can't access lexical declaration ? before initialization"],
    [`can't access property (?:"${identifier}"|\\?), ${slot} is (undefined|null)`, "can't access property ?, ? is $1"],
    [`(undefined|null) is not an object \\(evaluating ${quotedSlot}\\)`, '$1 is not an object (evaluating ?)'],
] as const).map(([template, fingerprint]) => ({ pattern: new RegExp(`^(?:${template})$`), fingerprint }));
const fixedErrors = new Set(['Assignment to constant variable.', 'Attempted to assign to readonly property.',
    'Unexpected token', 'Maximum call stack size exceeded', 'too much recursion', 'URI malformed',
    'Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch resource.']);

/** Match complete messages; captures contain only fixed engine tokens, never slot values. */
export function telemetryErrorFingerprint(value: string): string {
    const match = errorEnvelope.exec(value), name = match?.[1] ?? 'Error', message = match?.[2] ?? '';
    if (!/[\p{C}]/u.test(message)) {
        if (fixedErrors.has(message)) return name + ':' + message;
        for (const { pattern, fingerprint } of errorTemplates)
            if (pattern.test(message)) return name + ':' + message.replace(pattern, fingerprint);
    }
    return name + ':unrecognized';
}

const proseVocabulary: ReadonlySet<string> = new Set(REFUSAL_PROSE_WORDS);

/** Only fixed engine words can reach a prose slug, regardless of input encoding or account shape. */
function proseReason(kind: RefusalKind, value: string): string {
    const words = value.toLowerCase().match(/[\p{L}\p{N}\p{M}]+/gu)
        ?.filter(word => proseVocabulary.has(word)).slice(0, 4).join('-') || 'unclassified';
    return `${kind}:prose:${words}`;
}

/** Normalize known legacy formats; retain a bounded cause for unexpected prose. */
export function refusalReason(kind: RefusalKind, value: string, detail?: unknown): string {
    if (value === 'beacon:dropped:' + kind) return value;
    if (kind === 'warning') return maskTelemetryText(value, 191);
    if (kind === 'page_error') return pageErrorReason(value, detail);
    if (kind === 'rejection') return telemetryErrorFingerprint(value);
    let reason = value.split(/[\r\n]/)[0] ?? '';
    if (kind === 'refused' || kind === 'skipped') {
        // Parse complete formats first: authored names can themselves contain newlines, colons and semicolons.
        if (/: opportunity on-hit tail skipped \(/.test(value)) return 'weapon:opportunity-on-hit-tail-skipped';
        if (/: gathering succeeded; pickup skipped:/.test(value)) return 'gathering:pickup-ap-unavailable';
        const family = value.split(':')[0] ?? '', duty = value.split(':').pop() ?? '';
        const removalDuty = /:status-removal:(connected-peer-not-member|corpse-bits-and-inventory:not-ported|duration-inventory-destruction:not-ported)$/.exec(value)?.[1];
        if (kind === 'skipped' && removalDuty) reason = 'status-removal:' + removalDuty;
        else if (kind === 'skipped' && family === 'gm-status-remove' && value.includes(':special-status-teardown:')) reason = 'gm-status-remove:special-status-teardown';
        else if (kind === 'skipped' && ['dismount', 'gm-status-remove', 'gm-kill', 'gm-resurrect', 'gm-flee', 'gm-destroy', 'teleport-offer'].includes(family))
            reason = family + ':' + (['rider-placement-not-held', 'summon-tag-teardown-not-modeled',
                'connection-peers-not-held', 'connection-peer-teardown-not-modeled', 'death-and-corpse-followups-not-modeled',
                'recovery-and-posture-followups-not-modeled', 'crafting-completion-not-ported', 'duration-object-destruction-not-ported'].includes(duty) ? duty : 'effect-unported');
        else if (kind === 'skipped' && value.startsWith('gm-relocation:')) reason = 'gm-relocation:effect-unported';
        else if (value.startsWith('weapon:family:enchantment.abilities:') && value.endsWith(':unavailable')) reason = 'weapon:family:enchantment.abilities:unavailable';
        else if (value.startsWith('weapon:family:')) reason = value.split(':').slice(0, 3).join(':');
        // Movement appends a diagnostic to its code; treating that space as prose produced the misleading `not-creature` slug.
        else reason = reason.replace(/^(movement:not-ported:[a-z0-9:._-]+) why=[a-z0-9:._-]+$/, '$1');
        reason = reason.split(';')[0] ?? '';
        reason = reason.split(/:\s*[\[{]/)[0] ?? '';
        // Classify before the storage limit; a long prose prefix can otherwise look like a code.
        if (!isRefusalCode(maskTelemetryText(reason, reason.length))) return proseReason(kind, reason);
    }
    return maskTelemetryText(reason, 191);
}

/** Stateless page-shape reader: reuse the same readers as the actual encounter/caster surfaces. */
export function heldRefusalContext(encounter: unknown, actor: unknown): { encounter_id: string | null; actor_id: string | null; engine: string | null } {
    const host = asRow(encounter), caster = asRow(actor);
    const carried = asRow(host?._inventory_held) ?? asRow(host?._inventory_base)
        ?? asRow(caster?._inventory_held) ?? asRow(caster?._inventory_base);
    return { encounter_id: identity(encounterField(host, 'encounter_id') ?? encounterCreatureField(caster?.encounter_creatures, 'encounter_id')),
        actor_id: identity(encounterCreatureField(caster?.encounter_creatures, 'encounter_creature_id')), engine: tag(carried?.engine) };
}

/** Fixed diagnostics only; arbitrary nested event detail can contain names, prose and account fields. */
function diagnosticDetail(value: unknown): string {
    const row = asRow(value), out: Record<string, unknown> = {};
    if (typeof row?.timed_finish_error === 'string') out.timed_finish_error = maskTelemetryText(row.timed_finish_error, 160);
    // Carrier-catalog refusals expose fixed engine evidence, never an arbitrary
    // definition/body. Keep UUIDs here so a missing carrier/root can be located.
    if (identity(row?.carrier)) out.carrier = identity(row?.carrier);
    if (typeof row?.source === 'string' && /^(none|mirror|store-fetch|prewarm-join|pair-mirror|pair-fetch:[0-9]{1,3})$/.test(row.source)) out.source = row.source;
    for (const key of ['inflight', 'store']) if (row?.[key] === 0 || row?.[key] === 1) out[key] = row[key];
    if (row?.dropped_ms_ago === -1) out.dropped_ms_ago = -1;
    else if (duration(row?.dropped_ms_ago) !== null) out.dropped_ms_ago = duration(row?.dropped_ms_ago);
    if (typeof row?.missing === 'string') out.missing = row.missing.split(',').slice(0, 6)
        .filter(id => uuid.test(id) || id === 'catalog-base-unverified').map(id => identity(id) ?? id).join(',');
    const weaponId = identity(row?.weaponId ?? row?.weaponInventoryId);
    if (weaponId) out.weaponId = weaponId;
    if (typeof row?.file === 'string') {
        const file = maskTelemetryText(telemetryBasename(row.file), 96);
        if (/^[a-z0-9_.\-*]*$/i.test(file)) out.file = file;
    }
    for (const key of ['line', 'col', 'count', 'lateMs', 'gate_wait_ms', 'decide_ms', 'apply_ms', 'paint_ms',
        'commit_rtt_ms', 'echo_apply_ms', 'compile_ms', 'commit_pending', 'total_ms', 'shell_ms', 'prepare_ms',
        'renderer_ms', 'terrain_ms', 'environment_ms', 'structures_ms', 'placements_ms', 'finalize_ms', 'reveal_ms'])
        if (row?.[key] !== undefined && duration(row[key]) !== null) out[key] = duration(row[key]);
    if (typeof row?.instruction === 'boolean') out.instruction = row.instruction;
    if (telemetryCode(row?.caller)) out.caller = telemetryCode(row?.caller);
    const conflict = asRow(row?.conflict), response = asRow(conflict?.response);
    if (response) {
        const catalog: Record<string, unknown> = {};
        for (const key of ['error', 'changed_key', 'requested_version', 'current_version', 'detail'])
            if (typeof response[key] === 'string') catalog[key] = maskTelemetryText(response[key], key.endsWith('_version') ? 80 : key === 'detail' ? 64 : 32);
        for (const key of ['requested_revision', 'current_revision'])
            if (typeof response[key] === 'number' && Number.isSafeInteger(response[key]) && response[key] >= 0) catalog[key] = response[key];
        if (Object.keys(catalog).length) out.catalog = catalog;
    }
    if (Array.isArray(row?.actions)) {
        const actions: TelemetryAction[] = [];
        for (const value of row.actions.slice(-5)) {
            const action = asRow(value), lane = telemetryCode(action?.lane), schema = telemetryCode(action?.schema);
            if (lane && schema && typeof action?.operation_id === 'string' && /^op-[0-9]{1,9}$/.test(action.operation_id)
                && TRUTH_OUTCOMES.includes(action.outcome as typeof TRUTH_OUTCOMES[number])) {
                const reason = telemetryCode(action.reason);
                actions.push({ lane, schema, operation_id: action.operation_id, outcome: String(action.outcome), ...(reason ? { reason } : {}) });
            }
        }
        out.actions = actions;
        // The existing detail column is varchar(512); keep the newest complete records that fit.
        while (actions.length && JSON.stringify(out).length > 512) actions.shift();
    }
    return JSON.stringify(out);
}

/** No DOM, clock, random source or transport. One bounded, privacy-filtered storage shape. */
export function buildRefusalEvent(input: RefusalInput) {
    if (!uuid.test(input.id) || !REFUSAL_KINDS.includes(input.kind as RefusalKind) || typeof input.reason !== 'string') return null;
    const page = telemetryBasename(input.page);
    if (page.length > 64 || !/^[a-z0-9_]+\.php$/.test(page)) return null;
    const reason = refusalReason(input.kind as RefusalKind, input.reason, input.detail);
    if (!reason || !['ft3d', 'sheet', 'campaign', 'item', 'admin', 'other'].includes(String(input.surface))) return null;
    for (const key of ['encounter_id', 'actor_id', 'ability_id'] as const)
        if (input[key] !== undefined && input[key] !== null && identity(input[key]) === null) return null;
    const actor = identity(input.actor_id), encounter = identity(input.encounter_id);
    return { id: input.id.toUpperCase(), kind: input.kind as RefusalKind, reason, detail: diagnosticDetail(input.detail), page,
        surface: String(input.surface), encounter_id: actor && encounter ? encounter : null, actor_id: actor && encounter ? actor : null,
        ability_id: identity(input.ability_id), client_ms: duration(input.client_ms), build: tag(input.build), engine: tag(input.engine) };
}

/** Tab-local budget with an injected monotonic time; reserve only a valid event. */
export function createRefusalBudget(reportDropped?: (kind: RefusalKind, count: number) => void) {
    const last = new Map<string, number>();
    const buckets = new Map<RefusalKind, { at: number; tokens: number; hard: number; dropped: number; seen: number }>();
    return (event: { kind: RefusalKind; reason: string }, now: number): boolean => {
        const key = event.kind + ':' + event.reason, previous = last.get(key);
        const warning = event.kind === 'warning';
        const interval = warning || (event.kind === 'slow' && event.reason.startsWith('longtask:')) ? 10_000
            : event.kind === 'slow' && event.reason.startsWith('action:') ? 5000 : 60_000;
        // Ten-minute refills, independently for every kind. Novel reasons may use the
        // runaway allowance even after repeats exhaust the ordinary bucket.
        const capacity = warning ? 25 : 50;
        const bucket = buckets.get(event.kind) ?? { at: now, tokens: capacity, hard: 100, dropped: 0, seen: 0 };
        buckets.set(event.kind, bucket);
        const elapsed = Math.max(0, now - bucket.at);
        bucket.at = Math.max(now, bucket.at);
        bucket.tokens = Math.min(capacity, bucket.tokens + elapsed * capacity / 600_000);
        bucket.hard = Math.min(100, bucket.hard + elapsed * 100 / 600_000);
        // Reserve the summary's row too; neither summaries nor novel reasons bypass
        // the hard rate. Bound tab memory under a loop inventing arbitrary reasons.
        const cost = bucket.dropped ? 2 : 1;
        if (bucket.hard < cost || (previous !== undefined && (now - previous < interval || bucket.tokens < 1))
            || (previous === undefined && bucket.seen >= 4096)) {
            bucket.dropped = Math.min(4294967295, bucket.dropped + 1);
            return false;
        }
        if (previous === undefined) bucket.seen++;
        last.set(key, now);
        bucket.tokens = Math.max(0, bucket.tokens - 1);
        bucket.hard -= cost;
        const dropped = bucket.dropped;
        bucket.dropped = 0;
        if (dropped) try { reportDropped?.(event.kind, dropped); } catch { /* Observer only. */ }
        return true;
    };
}
